import React, { useState } from "react";
import { authHeaders } from '../../lib/apiAuth';
import { T, SPARKY_SERVER_URL } from '../shared/constants';

export default function ParentInsightReport({ progress, yearId }) {
  const [report, setReport]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState(false);

  const totalQ = Object.values(progress).reduce((a,b) => a+(b.total||0), 0);

  async function generateReport() {
    if (loading || generated) return;
    setLoading(true);
    setGenerated(true);

    const summary = Object.entries(progress).map(([subj, p]) => {
      const pct = p.total > 0 ? Math.round((p.correct/p.total)*100) : null;
      const topics = Object.entries(p.topics||{}).map(([t, tp]) => {
        const tpct = tp.total > 0 ? Math.round((tp.correct/tp.total)*100) : 0;
        return `  - ${t}: ${tpct}% (${tp.correct}/${tp.total})`;
      }).join('\n');
      return `${subj.toUpperCase()}: ${pct !== null ? pct+'%' : 'no data'} (${p.total||0} questions)\n${topics}`;
    }).join('\n\n');

    const system = `You are Lumi, an expert NAPLAN tutor writing a parent progress report.
Be specific, warm, and actionable. Name real topics and real patterns.
Return ONLY valid JSON \u2014 no markdown.

{
  "overall_summary": "2-sentence honest assessment of where the child is",
  "strengths": [
    { "area": "specific topic", "detail": "what they do well and why it matters" }
  ],
  "gaps": [
    { "area": "specific topic", "pattern": "the specific recurring error", "action": "concrete thing parent/child can do" }
  ],
  "priority_this_week": "The single most important thing to focus on",
  "positive_trend": "Something improving or worth celebrating",
  "naplan_readiness": "Not Yet Ready|Developing|On Track|Strong|Exceeding"
}`;

    const user = `Year ${yearId?.replace('y','')} student performance:\n${summary}\n\nWrite a specific, helpful parent report. Name exact topics and patterns.`;

    try {
      const res = await fetch(`${SPARKY_SERVER_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({
          model: 'claude-haiku-5-5',
          max_tokens: 600,
          system,
          messages: [{ role: 'user', content: user }],
        }),
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || '';
      const clean = text.replace(/```json|```/g, '').trim();
      setReport(JSON.parse(clean));
    } catch(e) {
      setReport(null);
    } finally {
      setLoading(false);
    }
  }

  const readinessColor = {
    'Not Yet Ready': T.red,
    'Developing':    '#F59E0B',
    'On Track':      '#3B82F6',
    'Strong':        '#10B981',
    'Exceeding':     '#10B981',
  };

  if (totalQ < 10) return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: '16px', marginBottom: 16, textAlign: 'center' }}>
      <p style={{ fontSize: 13, color: T.textMid, margin: 0 }}>Complete at least 10 questions to generate a full insight report.</p>
      <p style={{ fontSize: 12, color: T.textMid, margin: '6px 0 0' }}>{totalQ}/10 questions done</p>
    </div>
  );

  if (!generated) return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: '16px', marginBottom: 16 }}>
      <p style={{ fontSize: 14, fontWeight: 700, color: T.text, margin: '0 0 4px' }}>{"\uD83D\uDCCA"} Lumi's Insight Report</p>
      <p style={{ fontSize: 12, color: T.textMid, margin: '0 0 12px' }}>Deep analysis of your child's specific gaps and patterns {"\u2014"} not just scores.</p>
      <button onClick={generateReport}
        style={{ width: '100%', padding: 11, background: 'linear-gradient(135deg,#FBBF24,#F59E0B)', border: 'none', borderRadius: 10, color: '#0A0C10', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
        Generate Insight Report {"\u2192"}
      </button>
    </div>
  );

  if (loading) return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: '20px', marginBottom: 16, textAlign: 'center' }}>
      <p style={{ fontSize: 13, color: T.textMid, margin: '0 0 12px' }}>{"\u2728"} Lumi is analysing your child's performance...</p>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
        {[0,1,2].map(i => <div key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#F59E0B', animation: `lumi-idle ${0.6+i*0.2}s ease-in-out infinite` }}/>)}
      </div>
    </div>
  );

  if (!report) return null;

  const rc = readinessColor[report.naplan_readiness] || T.accent;

  return (
    <div style={{ marginBottom: 16, animation: 'fadeUp 0.4s ease' }}>
      <div style={{ background: `${rc}15`, border: `1px solid ${rc}44`, borderRadius: 12, padding: '14px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: 11, color: rc, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 4px' }}>NAPLAN Readiness</p>
          <p style={{ fontSize: 20, fontWeight: 700, color: rc, margin: '0 0 6px', fontFamily: "'Space Grotesk',sans-serif" }}>{report.naplan_readiness}</p>
          <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.55 }}>{report.overall_summary}</p>
        </div>
      </div>
      {report.strengths?.length > 0 && (
        <div style={{ background: `${T.green}10`, border: `1px solid ${T.green}33`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
          <p style={{ fontSize: 12, color: T.green, fontWeight: 700, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{"\u2705"} Strengths</p>
          {report.strengths.map((s, i) => (
            <div key={i} style={{ marginBottom: i < report.strengths.length-1 ? 8 : 0 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: '0 0 2px' }}>{s.area}</p>
              <p style={{ fontSize: 12, color: T.textMid, margin: 0, lineHeight: 1.5 }}>{s.detail}</p>
            </div>
          ))}
        </div>
      )}
      {report.gaps?.length > 0 && (
        <div style={{ background: `${T.red}08`, border: `1px solid ${T.red}22`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
          <p style={{ fontSize: 12, color: T.red, fontWeight: 700, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{"\uD83C\uDFAF"} Areas to Work On</p>
          {report.gaps.map((g, i) => (
            <div key={i} style={{ marginBottom: i < report.gaps.length-1 ? 10 : 0, paddingBottom: i < report.gaps.length-1 ? 10 : 0, borderBottom: i < report.gaps.length-1 ? `1px solid ${T.border}` : 'none' }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: T.text, margin: '0 0 2px' }}>{g.area}</p>
              <p style={{ fontSize: 12, color: T.textMid, margin: '0 0 4px', lineHeight: 1.5 }}>{g.pattern}</p>
              <p style={{ fontSize: 12, color: T.gold, margin: 0, fontWeight: 600 }}>{"\u2192"} {g.action}</p>
            </div>
          ))}
        </div>
      )}
      <div style={{ background: `${T.accent}10`, border: `1px solid ${T.accent}33`, borderRadius: 12, padding: '12px 14px', marginBottom: 10 }}>
        <p style={{ fontSize: 12, color: T.accent, fontWeight: 700, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{"\uD83D\uDCCC"} Priority This Week</p>
        <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.55 }}>{report.priority_this_week}</p>
      </div>
      {report.positive_trend && (
        <div style={{ background: `${T.gold}10`, border: `1px solid ${T.gold}33`, borderRadius: 12, padding: '12px 14px' }}>
          <p style={{ fontSize: 12, color: T.gold, fontWeight: 700, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{"\u2B50"} Something to Celebrate</p>
          <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.55 }}>{report.positive_trend}</p>
        </div>
      )}
      <button onClick={() => { setGenerated(false); setReport(null); }}
        style={{ width: '100%', marginTop: 10, padding: '10px', background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, color: T.textMid, fontSize: 12, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
        {"\uD83D\uDD04"} Refresh Report
      </button>
    </div>
  );
}
